import React, { useCallback, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import OndemandVideoRoundedIcon from "@mui/icons-material/OndemandVideoRounded";

import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Select,
  Tabs,
  Tab,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Modal,
  Avatar,
  CircularProgress,
  Divider,
  IconButton,
  Chip,
  Fade,
  Backdrop,
} from "@mui/material";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

import axios from "axios";
import { useSelector } from "react-redux";
import AdminMonthSelect, {
  ADMIN_MONTH_OPTIONS,
  normalizeVoteRow,
  normalizeVoteRows,
} from "../../component/admin/AdminMonthSelect";
import {
  currentAdminMonthValue,
  voteCountLabel,
} from "../../utils/adminMonthFilter";

export default function Analytics() {
  const token = useSelector((state) => state.auth.token);

  const [period, setPeriod] = useState("day");
  const [voteType, setVoteType] = useState("user");

  const [analytics, setAnalytics] = useState([]);
  const [cards, setCards] = useState({});

  const [tab, setTab] = useState(0);

  const [usersData, setUsersData] = useState([]);
  const [guestVideos, setGuestVideos] = useState([]);

  const [search, setSearch] = useState("");
  const [month, setMonth] = useState(currentAdminMonthValue());
  const votesLabel = voteCountLabel(month, ADMIN_MONTH_OPTIONS);

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingGuest, setLoadingGuest] = useState(false);

  const [openVideos, setOpenVideos] = useState(false);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [modalTitle, setModalTitle] = useState("User Voted Videos");

  useEffect(() => {
    if (tab === 0) {
      setVoteType("user");
      setSearch("");
    } else if (tab === 1) {
      setVoteType("guest");
      setSearch("");
    }
  }, [tab]);

  const fetchAnalytics = useCallback(async () => {
    if (!token) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/analytics/votes`,
        {
          params: { period, voteType, search, month },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setAnalytics(response.data.analytics || []);
      }
    } catch (error) {
      console.error(error);
      setAnalytics([]);
    }
  }, [token, period, voteType, search, month]);

  const fetchCards = useCallback(async () => {
    if (!token) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/analytics/cards`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setCards(response.data.data || {});
      }
    } catch (error) {
      console.error(error);
    }
  }, [token]);

  const fetchUsersAnalytics = useCallback(async () => {
    if (!token) return;

    try {
      setLoadingUsers(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/analytics/users-votes`,
        {
          params: { search, month },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setUsersData(normalizeVoteRows(response.data.data));
      } else {
        setUsersData([]);
      }
    } catch (error) {
      console.error(error);
      setUsersData([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [token, search, month]);

  const fetchGuestAnalytics = useCallback(async () => {
    if (!token) return;

    try {
      setLoadingGuest(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/analytics/guest-videos`,
        {
          params: { month },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setGuestVideos(normalizeVoteRows(response.data.data));
      } else {
        setGuestVideos([]);
      }
    } catch (error) {
      console.error(error);
      setGuestVideos([]);
    } finally {
      setLoadingGuest(false);
    }
  }, [token, month]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    if (tab === 0) {
      fetchUsersAnalytics();
    }
  }, [tab, fetchUsersAnalytics]);

  useEffect(() => {
    if (tab === 1) {
      fetchGuestAnalytics();
    }
  }, [tab, fetchGuestAnalytics]);

  const openVideosPopup = useCallback(
    async (userId) => {
      if (!token) return;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/analytics/user-videos/${userId}`,
          {
            params: { month },
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success) {
          setSelectedVideos(normalizeVoteRows(response.data.data));
          setModalTitle(
            month
              ? `User Voted Videos (${voteCountLabel(month, ADMIN_MONTH_OPTIONS).replace("Votes in ", "")})`
              : "User Voted Videos"
          );
          setOpenVideos(true);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [token, month]
  );

  function openGuestVideoPopup(video) {
    setSelectedVideos([normalizeVoteRow(video)]);
    setModalTitle("Guest Video Details");
    setOpenVideos(true);
  }

  return (
    <React.Fragment>
      <Box className="page_title">
        {/* PAGE TITLE */}
        <Typography variant="h1" component="h1">
          Analytics Dashboard
        </Typography>

        {/* TOP CARDS */}
        <Grid container spacing={2.5}>
          {[
            {
              title: "Total Users",
              value: cards.totalUsers,
              color: "#6366f1",
              bg: "#eef2ff",
            },
            {
              title: "Total Guest Users",
              value: cards.totalGuestUsers,
              color: "#0ea5e9",
              bg: "#e0f2fe",
            },
            {
              title: "User Votes",
              value: cards.totalUserVotes,
              color: "#ef4444",
              bg: "#fef2f2",
            },
            {
              title: "Guest Votes",
              value: cards.totalGuestVotes,
              color: "#f59e0b",
              bg: "#fffbeb",
            },
          ].map((card, index) => (
            <Grid item xs={12} md={3} key={index}>
              <Card
                sx={{
                  borderRadius: "14px",
                  border: "1px solid #edf0f2",
                  boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
                  background: "#fff",
                  transition: "box-shadow 0.2s",
                  "&:hover": {
                    boxShadow: "0 6px 20px rgba(15,23,42,0.09)",
                  },
                }}
              >
                <CardContent sx={{ padding: "20px !important" }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      background: card.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1.5,
                    }}
                  >
                    <OndemandVideoRoundedIcon
                      sx={{ color: card.color, fontSize: 20 }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#64748b",
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    {card.title}
                  </Typography>

                  <Typography
                    sx={{ fontSize: "26px", fontWeight: 700, color: "#111827" }}
                  >
                    {card.value?.toLocaleString() || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* GRAPH */}
        <Card
          sx={{
            mt: 3,
            borderRadius: "14px",
            border: "1px solid #edf0f2",
            boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
          }}
        >
          <CardContent sx={{ padding: "24px !important" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                mb: 3,
              }}
            >
              <Typography
                sx={{ fontSize: "18px", fontWeight: 600, color: "#0f172a" }}
              >
                Votes Analytics
              </Typography>

              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <AdminMonthSelect
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                />

                <Select
                  size="small"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  sx={{
                    height: 38,
                    minWidth: 130,
                    fontSize: "13px",
                    background: "#fff",
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: "#dbe2ea",
                    },
                  }}
                >
                  <MenuItem value="day">Day Wise</MenuItem>
                  <MenuItem value="week">Week Wise</MenuItem>
                  <MenuItem value="month">Month Wise</MenuItem>
                </Select>

                <Select
                  size="small"
                  value={voteType}
                  onChange={(e) => setVoteType(e.target.value)}
                  sx={{
                    height: 38,
                    minWidth: 130,
                    fontSize: "13px",
                    background: "#fff",
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: "#dbe2ea",
                    },
                  }}
                >
                  <MenuItem value="user">User Votes</MenuItem>
                  <MenuItem value="guest">Guest Votes</MenuItem>
                </Select>
              </Box>
            </Box>

            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={analytics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                />
                <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#ef4444"
                  strokeWidth={2.5}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* TABS SECTION */}
        <Card
          sx={{
            mt: 3,
            borderRadius: "14px",
            border: "1px solid #edf0f2",
            boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
          }}
        >
          <CardContent sx={{ padding: "24px !important" }}>
            <Tabs
              value={tab}
              onChange={(e, value) => setTab(value)}
              sx={{
                minHeight: "42px",
                "& .MuiTab-root": {
                  minHeight: "42px",
                  fontSize: "13px",
                  fontWeight: 600,
                  textTransform: "none",
                  color: "#64748b",
                },
                "& .Mui-selected": { color: "#ef4444 !important" },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#ef4444",
                  height: "2px",
                },
              }}
            >
              <Tab label="Users Vote Analytics" />
              <Tab label="Guest User Video Analytics" />
            </Tabs>

            <Divider sx={{ my: 3 }} />

            {/* USERS TAB */}
            {tab === 0 && (
              <>
                <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                  <TextField
                    placeholder="Search user..."
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                      minWidth: 240,
                      "& .MuiOutlinedInput-root": {
                        height: 38,
                        fontSize: "13px",
                        background: "#fff",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#dbe2ea",
                      },
                    }}
                  />

                  <AdminMonthSelect
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  />
                </Box>

                {loadingUsers ? (
                  <Box textAlign="center" py={5}>
                    <CircularProgress size={28} sx={{ color: "#ef4444" }} />
                  </Box>
                ) : (
                  <TableContainer
                    component={Paper}
                    sx={{
                      background: "#fff",
                      borderRadius: "12px",
                      overflow: "hidden",
                      border: "1px solid #edf0f2",
                      boxShadow: "none",
                    }}
                  >
                    <Table>
                      <TableHead
                        sx={{
                          background: "#f8fafc",
                          "& th": {
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#475569",
                            padding: "14px 16px",
                            borderBottom: "1px solid #e2e8f0",
                          },
                        }}
                      >
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>{votesLabel}</TableCell>
                          <TableCell>Videos</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {usersData.length > 0 ? (
                          usersData.map((user) => (
                            <TableRow
                              key={user.id}
                              hover
                              sx={{
                                "& td": {
                                  padding: "14px 16px",
                                  fontSize: "13px",
                                  color: "#334155",
                                  borderBottom: "1px solid #f1f5f9",
                                },
                                "&:hover": { background: "#fafafa" },
                              }}
                            >
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                  }}
                                >
                                  <Avatar
                                    src={user.profile_img}
                                    sx={{
                                      width: 36,
                                      height: 36,
                                      border: "2px solid #f1f5f9",
                                    }}
                                  />
                                  <Typography
                                    sx={{ fontSize: "13px", fontWeight: 600 }}
                                  >
                                    {user.firstName} {user.lastName}
                                  </Typography>
                                </Box>
                              </TableCell>

                              <TableCell sx={{ color: "#64748b !important" }}>
                                {user.email}
                              </TableCell>

                              <TableCell>
                                <Chip
                                  label={user.totalVotes}
                                  size="small"
                                  sx={{
                                    background: "#fef2f2",
                                    color: "#ef4444",
                                    fontWeight: 700,
                                    fontSize: "12px",
                                    height: 26,
                                    borderRadius: "6px",
                                  }}
                                />
                              </TableCell>

                              <TableCell>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={
                                    <PlayArrowRoundedIcon
                                      sx={{ fontSize: "16px !important" }}
                                    />
                                  }
                                  onClick={() => openVideosPopup(user.id)}
                                  sx={{
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    borderColor: "#cbd5e1",
                                    color: "#334155",
                                    padding: "5px 12px",
                                    "&:hover": {
                                      borderColor: "#ef4444",
                                      color: "#ef4444",
                                      background: "#fef2f2",
                                    },
                                  }}
                                >
                                  {user.totalVideos} Videos
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              align="center"
                              sx={{ py: 5, color: "#94a3b8" }}
                            >
                              No users found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {/* GUEST TAB */}
            {tab === 1 && (
              <>
                <Box mb={3}>
                  <AdminMonthSelect
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  />
                </Box>

                {loadingGuest ? (
                  <Box textAlign="center" py={5}>
                    <CircularProgress size={28} sx={{ color: "#ef4444" }} />
                  </Box>
                ) : (
                  <TableContainer
                    component={Paper}
                    sx={{
                      background: "#fff",
                      borderRadius: "12px",
                      overflow: "hidden",
                      border: "1px solid #edf0f2",
                      boxShadow: "none",
                    }}
                  >
                    <Table>
                      <TableHead
                        sx={{
                          background: "#f8fafc",
                          "& th": {
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#475569",
                            padding: "14px 16px",
                            borderBottom: "1px solid #e2e8f0",
                          },
                        }}
                      >
                        <TableRow>
                          <TableCell>Thumbnail</TableCell>
                          <TableCell>Video</TableCell>
                          <TableCell>Club</TableCell>
                          <TableCell>Goal Scorer</TableCell>
                          <TableCell>{votesLabel}</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {guestVideos.length > 0 ? (
                          guestVideos.map((video) => (
                            <TableRow
                              key={video.id}
                              hover
                              sx={{
                                "& td": {
                                  padding: "14px 16px",
                                  fontSize: "13px",
                                  color: "#334155",
                                  borderBottom: "1px solid #f1f5f9",
                                },
                                "&:hover": { background: "#fafafa" },
                              }}
                            >
                              <TableCell>
                                <Box
                                  sx={{
                                    position: "relative",
                                    width: 110,
                                    height: 65,
                                    borderRadius: "10px",
                                    overflow: "hidden",
                                    cursor: "pointer",
                                    "&:hover .play-overlay": { opacity: 1 },
                                  }}
                                  onClick={() => openGuestVideoPopup(video)}
                                >
                                  <img
                                    src={video.thumbnail}
                                    alt=""
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      display: "block",
                                    }}
                                  />
                                  <Box
                                    className="play-overlay"
                                    sx={{
                                      position: "absolute",
                                      inset: 0,
                                      background: "rgba(0,0,0,0.45)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      opacity: 0,
                                      transition: "opacity 0.2s",
                                      borderRadius: "10px",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: "50%",
                                        background: "#ef4444",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <PlayArrowRoundedIcon
                                        sx={{ color: "#fff", fontSize: 18 }}
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                              </TableCell>

                              <TableCell>
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    maxWidth: 220,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {video.title}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Chip
                                  label={video.club || "N/A"}
                                  size="small"
                                  sx={{
                                    background: "#f0f9ff",
                                    color: "#0369a1",
                                    fontWeight: 600,
                                    fontSize: "11px",
                                    height: 24,
                                    borderRadius: "6px",
                                  }}
                                />
                              </TableCell>

                              <TableCell>
                                <Typography
                                  sx={{ fontSize: "13px", color: "#475569" }}
                                >
                                  {video.goal_scorer || "N/A"}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Chip
                                  label={video.totalVotes}
                                  size="small"
                                  sx={{
                                    background: "#fef2f2",
                                    color: "#ef4444",
                                    fontWeight: 700,
                                    fontSize: "12px",
                                    height: 26,
                                    borderRadius: "6px",
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              align="center"
                              sx={{ py: 5, color: "#94a3b8" }}
                            >
                              No guest videos found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* VIDEOS POPUP — Redesigned */}
        <Modal
          open={openVideos}
          onClose={() => setOpenVideos(false)}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{ backdrop: { timeout: 300 } }}
        >
          <Fade in={openVideos}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "92%", sm: "78%", md: "65%" },
                maxWidth: "820px",
                bgcolor: "#fff",
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 24px 60px rgba(15,23,42,0.2)",
                outline: "none",
                display: "flex",
                flexDirection: "column",
                maxHeight: "76vh",
              }}
            >
              {/* MODAL HEADER — compact red */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  background: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "8px",
                      background: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PlayArrowRoundedIcon
                      sx={{ color: "#fff", fontSize: 18 }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: "#fff",
                        lineHeight: 1.2,
                      }}
                    >
                      {modalTitle}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: "rgba(255,255,255,0.75)",
                        lineHeight: 1.2,
                      }}
                    >
                      {selectedVideos.length} video
                      {selectedVideos.length !== 1 ? "s" : ""}
                    </Typography>
                  </Box>
                </Box>

                <IconButton
                  onClick={() => setOpenVideos(false)}
                  size="small"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    "&:hover": {
                      color: "#fff",
                      background: "rgba(255,255,255,0.15)",
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* MODAL BODY */}
              <Box
                sx={{
                  p: 2,
                  overflowY: "auto",
                  background: "#f8fafc",
                  flex: 1,
                  "&::-webkit-scrollbar": { width: "6px" },
                  "&::-webkit-scrollbar-track": { background: "#f1f5f9" },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#cbd5e1",
                    borderRadius: "10px",
                  },
                }}
              >
                {selectedVideos.length > 0 ? (
                  <Grid
                    container
                    spacing={2}
                    alignItems="stretch"
                    justifyContent={
                      selectedVideos.length === 1 ? "center" : "flex-start"
                    }
                  >
                    {selectedVideos.map((video) => (
                      <Grid
                        item
                        xs={12}
                        sm={selectedVideos.length === 1 ? 8 : 6}
                        key={video.id}
                        sx={{ display: "flex" }}
                      >
                        <Card
                          sx={{
                            borderRadius: "14px",
                            overflow: "hidden",
                            border: "1px solid #e2e8f0",
                            boxShadow: "0 2px 10px rgba(15,23,42,0.06)",
                            background: "#fff",
                            transition: "all 0.25s ease",
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 8px 24px rgba(15,23,42,0.11)",
                            },
                          }}
                        >
                          {/* VIDEO PLAYER — 16:9, capped height */}
                          <Box
                            sx={{
                              width: "100%",
                              aspectRatio: "16/9",
                              maxHeight: 200,
                              background: "#000",
                              flexShrink: 0,
                              overflow: "hidden",
                            }}
                          >
                            <video
                              controls
                              preload="metadata"
                              poster={video.thumbnail}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                display: "block",
                              }}
                            >
                              <source src={video.video_link} type="video/mp4" />
                            </video>
                          </Box>

                          {/* CARD CONTENT */}
                          <CardContent
                            sx={{
                              p: "12px 14px 14px !important",
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.2,
                            }}
                          >
                            {/* TITLE */}
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 700,
                                color: "#0f172a",
                                lineHeight: 1.4,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {video.title}
                            </Typography>

                            {/* CLUB + GOAL SCORER */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                background: "#f8fafc",
                                borderRadius: "10px",
                                p: "10px 14px",
                                border: "1px solid #e2e8f0",
                              }}
                            >
                              {/* Club — left */}
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "10px",
                                    color: "#94a3b8",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.6px",
                                    mb: 0.5,
                                  }}
                                >
                                  Club
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#0369a1",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: 160,
                                  }}
                                >
                                  {video.club || "N/A"}
                                </Typography>
                              </Box>

                              {/* Divider */}
                              <Box
                                sx={{
                                  width: "1px",
                                  alignSelf: "stretch",
                                  background: "#e2e8f0",
                                  mx: 1.5,
                                  flexShrink: 0,
                                }}
                              />

                              {/* Goal Scorer — right aligned */}
                              <Box sx={{ textAlign: "right" }}>
                                <Typography
                                  sx={{
                                    fontSize: "10px",
                                    color: "#94a3b8",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.6px",
                                    mb: 0.5,
                                  }}
                                >
                                  Goal Scorer
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#15803d",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: 160,
                                  }}
                                >
                                  {video.goal_scorer || "N/A"}
                                </Typography>
                              </Box>
                            </Box>

                            {/* VOTES ROW */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                background: "#fef2f2",
                                borderRadius: "8px",
                                px: 1.5,
                                py: 1,
                                border: "1px solid #fecaca",
                                mt: "auto",
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "#ef4444",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                }}
                              >
                                {votesLabel}
                              </Typography>

                              <Typography
                                sx={{
                                  color: "#ef4444",
                                  fontWeight: 800,
                                  fontSize: "18px",
                                  lineHeight: 1,
                                }}
                              >
                                {video.totalVotes}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ width: "100%", py: 8, textAlign: "center" }}>
                    <OndemandVideoRoundedIcon
                      sx={{ fontSize: 48, color: "#cbd5e1", mb: 1.5 }}
                    />
                    <Typography sx={{ color: "#94a3b8", fontSize: "14px" }}>
                      No videos found
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Fade>
        </Modal>
      </Box>
    </React.Fragment>
  );
}
